import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import type { AiEmailUpdateDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useSlug } from "@/hooks/useSlug";
import { aiEmailUpdateMutations } from "@/queries/ai-email-updates/mutations";
import { useAiEmailUpdates } from "@/queries/ai-email-updates/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function AiEmailUpdateUnsubscribePage(): React.ReactNode {
  const queries = useAiEmailUpdates();

  const { data = [], isFetching } = useQuery(queries.list());
  const { mutate: unsubscribeAiEmailUpdate, isPending: isUnsubscribing } =
    aiEmailUpdateMutations.useUnsubscribeAiEmailUpdate();

  const { t } = useTranslation();
  const slug = useSlug();
  const navigate = useNavigate();

  const param = useParams<{ id?: string }>();

  const foundItem = param.id ? data.find((item) => item.id === param.id) : undefined;

  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<AiEmailUpdateDto>();

    return [
      helper.accessor("prompt", {
        header: t("page.ask-nobi-email-update.prompt"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("timePeriod", {
        header: t("page.ask-nobi-email-update.time-period"),
        cell: (cell) => {
          const timePeriod = cell.getValue();

          return (
            <span>
              {timePeriod === "weekly"
                ? t("page.ask-nobi-email-update.time-period-weekly")
                : t("page.ask-nobi-email-update.time-period-monthly")}
            </span>
          );
        },
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const id = cell.getValue();
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.unsubscribe"),
              },
              value: "unsubscribe",
              "data-testid": "context-menu-unsubscribe-btn",
              icon: <Trash02Icon />,
              type: "button",
              callbackFn: () => unsubscribeAiEmailUpdate(id),
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [t, unsubscribeAiEmailUpdate]);

  const tableInstance = useResizableTable(TABLE_KEYS.EMAIL_UNSUBSCRIBE, {
    columns,
    data,
    enableSorting: false,
  });

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.ask-nobi-email-update.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.ask-nobi-email-update.title"),
            },
          ]}
        />
      }
    >
      <PageGrid.Row>
        <PageGrid.Column size="100%">
          <Table table={tableInstance} data-testid="ai-email-update-list" isLoading={isFetching} />
        </PageGrid.Column>
      </PageGrid.Row>
      <ConfirmModal
        title={t("page.ask-nobi-email-update.unsubscribe.title")}
        description={t("page.ask-nobi-email-update.unsubscribe.description", {
          prompt: foundItem?.prompt || "-",
        })}
        isLoading={isUnsubscribing}
        tone="warning"
        onReject={() => void navigate(routes.emailUnsubscribe.aiUpdateList({ slug }))}
        rejectBtnProps={{
          "data-testid": "cancel-unsubscribe-btn",
        }}
        onOpenChange={(open) => {
          if (!open) {
            void navigate(routes.emailUnsubscribe.aiUpdateList({ slug }));
          }
        }}
        onResolve={() => {
          if (foundItem) {
            unsubscribeAiEmailUpdate(foundItem.id);
          }
        }}
        resolveBtnProps={{
          text: t("common.action.unsubscribe"),
          "data-testid": "confirm-unsubscribe-btn",
        }}
        isOpened={foundItem !== undefined}
        shouldCloseOnEsc
      />
    </DocumentPage>
  );
}
